<script setup>
// Botão CDC de busca card combinada, ao lado do WavoipCallButton no sidebar da
// conversa (COMBO-01). Self-gate por phone_number OU email do contato
// (COMBO-03). Ícone derivado do sistema-card, distinto do WAVOIP e dos botões
// inline (COMBO-02). Delega o resultado da busca para CardSearchResultDialog,
// o único dono da lógica de abrir aba/modal (COMBO-06, COMBO-07). Guard de
// re-entrância via isLoading (COMBO-09). Busca em cascata telefone->email:
// pula o campo ausente, para no primeiro resultado não vazio, toast de
// not_found só depois de esgotar os passos aplicáveis, e aborta a cascata
// inteira no primeiro erro (COMBO-04, COMBO-05, COMBO-08). Origem: ADR 0008,
// Phase 9.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardDashboardSearchAPI from '../api/cardDashboardSearch';
import CardSearchResultDialog from './CardSearchResultDialog.vue';
import SistemaCardIcon from './SistemaCardIcon.vue';

const props = defineProps({
  contact: { type: Object, default: null },
});

const { t } = useI18n();

const isLoading = ref(false);
const resultDialogRef = ref(null);

const shouldRender = computed(
  () => Boolean(props.contact?.phone_number) || Boolean(props.contact?.email)
);

const onClick = async () => {
  if (isLoading.value) return;
  const requestedContactId = props.contact?.id;
  const steps = [
    { field: 'phone', value: props.contact?.phone_number },
    { field: 'email', value: props.contact?.email },
  ].filter(step => Boolean(step.value));

  isLoading.value = true;
  try {
    // The email request must only fire once the phone response is known to
    // be empty (COMBO-04/COMBO-08) — gathering the two requests into a
    // combined/racing promise would break "stop on the first failure", so
    // the sequential for/await loop below is intentional, not an oversight.
    // eslint-disable-next-line no-restricted-syntax
    for (const step of steps) {
      // eslint-disable-next-line no-await-in-loop
      const response = await CardDashboardSearchAPI.search(
        requestedContactId,
        step.field
      );
      const list = response.data;
      if (!Array.isArray(list)) {
        throw new Error('Unexpected card dashboard search response shape');
      }
      // The component instance is not keyed by contact id in ContactPanel,
      // so an in-flight request can resolve after the agent has switched to
      // a different contact; bail instead of presenting a stale result.
      if (requestedContactId !== props.contact?.id) return;
      if (list.length > 0) {
        resultDialogRef.value?.present(list);
        return;
      }
    }
    if (requestedContactId === props.contact?.id) {
      useAlert(t('CDC.CARD_SEARCH.toast.not_found'));
    }
  } catch (error) {
    if (requestedContactId === props.contact?.id) {
      useAlert(t('CDC.CARD_SEARCH.toast.error'));
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <span class="contents">
    <NextButton
      v-if="shouldRender"
      v-tooltip.top-end="t('CDC.CARD_SEARCH.combo_tooltip')"
      size="sm"
      slate
      faded
      :disabled="isLoading"
      :is-loading="isLoading"
      :aria-label="t('CDC.CARD_SEARCH.combo_tooltip')"
      @click="onClick"
    >
      <template #icon>
        <SistemaCardIcon class="flex-shrink-0 w-4 h-4" />
      </template>
    </NextButton>
    <CardSearchResultDialog v-if="shouldRender" ref="resultDialogRef" />
  </span>
</template>
